export const EMAIL_FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
export const EMAIL_EDITOR_SIZE_OPTIONS = [
    '12px',
    '14px',
    '15px',
    '16px',
    '18px',
    '20px',
    '24px',
    '32px',
    '42px'
];
export const EMAIL_EDITOR_PRESET_STYLES = {
    heading: {
        fontSize: '42px',
        lineHeight: '50px',
        color: '#161c24',
        textAlign: 'center',
        fontWeight: 400,
        defaultSize: '42px'
    },
    body: {
        fontSize: '16px',
        lineHeight: '22px',
        color: '#637381',
        textAlign: 'center',
        fontWeight: 400,
        defaultSize: '16px'
    },
    detailsHeading: {
        fontSize: '20px',
        lineHeight: '30px',
        color: '#161c24',
        textAlign: 'left',
        fontWeight: 400,
        defaultSize: '20px'
    },
    detailsSubtext: {
        fontSize: '16px',
        lineHeight: '22px',
        color: '#575756',
        textAlign: 'left',
        fontWeight: 400,
        defaultSize: '16px'
    },
    badge: {
        fontSize: '14px',
        lineHeight: '16px',
        color: '#161c24',
        textAlign: 'center',
        fontWeight: 400,
        defaultSize: '14px'
    },
    buttonText: {
        fontSize: '15px',
        lineHeight: '20px',
        color: '#161c24',
        textAlign: 'center',
        fontWeight: 400,
        defaultSize: '15px'
    }
};
export const EMAIL_FONT_SIZE_LINE_HEIGHTS = {
    '12px': '16px',
    '14px': '20px',
    '15px': '20px',
    '16px': '22px',
    '18px': '24px',
    '20px': '30px',
    '24px': '32px',
    '32px': '40px',
    '40px': '48px',
    '42px': '50px'
};
export const getEmailLineHeightForSize = (size) => EMAIL_FONT_SIZE_LINE_HEIGHTS[size];
export const htmlContainsInlineFontSize = (html) => /font-size\s*:\s*[\d.]+px/i.test(html);
/**
 * Quill wraps every line in a <p>, which carries the browser's default block margin.
 * Badge/button text is substituted inside an inline <span>, so the <p> wrapper must be
 * stripped (not converted to a block span like headings) to avoid unwanted top/bottom gaps.
 */
export const stripEditorParagraphTags = (html) => {
    if (!html)
        return html;
    return html.replace(/<p(\s[^>]*)?>/gi, '').replace(/<\/p>/gi, '');
};
export const isSingleLineEmailEditorPreset = (preset) => preset === 'badge' || preset === 'buttonText';
/** Badge/button copy is one logical line (no Enter) but should wrap visually when it runs long. */
export const needsEmailSingleLineCollapse = (html) => {
    if (!html?.trim())
        return false;
    const paragraphCount = (html.match(/<p\b/gi) || []).length;
    return paragraphCount > 1 || /<br\s*\/?>/i.test(html);
};
export const flattenEmailSingleLineParagraphs = (html) => {
    if (!html?.trim())
        return html;
    const paragraphPattern = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
    const parts = [];
    let match;
    while ((match = paragraphPattern.exec(html)) !== null) {
        const inner = match[1].replace(/<br\s*\/?>/gi, ' ').trim();
        if (inner)
            parts.push(inner);
    }
    if (parts.length === 0) {
        return html.replace(/<br\s*\/?>/gi, ' ').trim();
    }
    const merged = parts.join(' ').replace(/\s+/g, ' ').trim();
    return `<p style="margin: 0;">${merged}</p>`;
};
/** Quill may split a heading into multiple <p> blocks; merge to one <p> with <br /> to avoid double line-height gaps. */
export const flattenEmailHeadingParagraphs = (html) => {
    if (!html?.trim())
        return html;
    const paragraphPattern = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
    const parts = [];
    let match;
    while ((match = paragraphPattern.exec(html)) !== null) {
        const inner = match[1].trim();
        if (inner)
            parts.push(inner);
    }
    if (parts.length <= 1)
        return html;
    return `<p style="margin: 0;">${parts.join('<br />')}</p>`;
};
export const applyEmailSizeAndLineHeight = (editor, index, length, size) => {
    editor.formatText(index, length, 'size', size, 'silent');
    const lineHeight = getEmailLineHeightForSize(size);
    if (lineHeight) {
        editor.formatText(index, length, 'lineheight', lineHeight, 'silent');
    }
};
export const applyMissingEmailPresetFormats = (editor, preset, options) => {
    const { defaultSize, textAlign } = EMAIL_EDITOR_PRESET_STYLES[preset];
    const docLength = editor.getLength();
    if (docLength <= 1)
        return false;
    let changed = false;
    let index = 0;
    while (index < docLength) {
        const [line] = editor.getLine(index);
        if (!line)
            break;
        const lineLength = line.length();
        const textLength = Math.max(1, lineLength - 1);
        const formats = editor.getFormat(index, textLength);
        if (!formats.size && !options?.respectUserSize) {
            applyEmailSizeAndLineHeight(editor, index, textLength, defaultSize);
            changed = true;
        }
        else if (!formats.lineheight) {
            const lineHeight = getEmailLineHeightForSize(String(formats.size));
            if (lineHeight) {
                editor.formatText(index, textLength, 'lineheight', lineHeight, 'silent');
                changed = true;
            }
        }
        const align = formats.align;
        if (!options?.respectUserAlign && align !== textAlign) {
            editor.formatLine(index, lineLength, 'align', textAlign, 'silent');
            changed = true;
        }
        index += lineLength;
    }
    return changed;
};
